"use strict";
/**
 * lastDayOfPeriodMonth.ts
 *
 * Returns the last day of the given period as a UTC midnight Date.
 *
 * Used as the `invoiced_date` fallback for monthly aggregator templates that
 * carry NO Invoice Date column in their source files. Period-end is the
 * broker's payment-period close — it corresponds to what real broker RCTIs
 * actually show (Feb-28/29, other months 30/31), is defensible to CASA Tier 2
 * and ISO 27001 auditors, and is consistent across all five non-RCTI-paired
 * aggregators (LMG / Choice / Vision / WLTH / Connective).
 *
 * History:
 *   2026-04-30  Original VFA fix (`2b3c18fc`) used `period_month + day-15`.
 *               Day-15 was a synthesized placeholder corresponding to nothing
 *               in the broker payment lifecycle.
 *   2026-05-14  Replaced day-15 with LAST_DAY across all 5 non-RCTI-paired
 *               aggregators (see docs/handovers/2026-05-14-INVOICED-DATE-LASTDAY-CONVENTION.md).
 *
 * RCTI-paired templates (Entors / Loan Market entities / ORDE Direct / AFG):
 *   These pull `queueInvoicedDate` from the paired RCTI metadata; the file
 *   processor's fallback is never used for them. See processAndWrite.ts line
 *   ~1547 for the queueInvoicedDate override.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.lastDayOfPeriodMonth = lastDayOfPeriodMonth;
/**
 * Returns the last day of the given period as a UTC midnight Date.
 *
 * @param periodMonth - "YYYY-MM" format (e.g. "2026-03")
 * @returns Date — last day of that month at UTC midnight (e.g. 2026-03-31T00:00:00Z)
 *
 * @example
 *   lastDayOfPeriodMonth("2026-03")  // → 2026-03-31T00:00:00Z
 *   lastDayOfPeriodMonth("2024-02")  // → 2024-02-29T00:00:00Z (leap)
 *   lastDayOfPeriodMonth("2025-02")  // → 2025-02-28T00:00:00Z (non-leap)
 *   lastDayOfPeriodMonth("2026-12")  // → 2026-12-31T00:00:00Z
 */
function lastDayOfPeriodMonth(periodMonth) {
    const match = periodMonth.match(/^(\d{4})-(\d{2})$/);
    if (!match) {
        throw new Error(`lastDayOfPeriodMonth: periodMonth must be "YYYY-MM" format; got ${JSON.stringify(periodMonth)}`);
    }
    const y = Number(match[1]);
    const m = Number(match[2]);
    // Date.UTC(y, m, 0) returns "day 0 of month m" which equals the last day of
    // month m-1 (0-indexed) = last day of month m (1-indexed). Example: m=3
    // (March, 1-indexed) → Date.UTC(2026, 3, 0) = March 31 at UTC midnight.
    return new Date(Date.UTC(y, m, 0));
}
